import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Bot, Check, CheckCircle2, ChevronRight, History, Lock, Sparkles, UserCheck } from 'lucide-react';

export const WORKSPACE_READINESS = Object.freeze({
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  READY: 'ready',
  NEEDS_ATTENTION: 'needs_attention',
  BLOCKED: 'blocked'
});

const READINESS_LABELS = {
  [WORKSPACE_READINESS.NOT_STARTED]: 'Not Started',
  [WORKSPACE_READINESS.IN_PROGRESS]: 'In Progress',
  [WORKSPACE_READINESS.READY]: 'Ready',
  [WORKSPACE_READINESS.NEEDS_ATTENTION]: 'Needs Attention',
  [WORKSPACE_READINESS.BLOCKED]: 'Blocked'
};

const withStage = (pathname, search, stage) => {
  const next = new URLSearchParams(search);
  next.set('stage', stage);
  return `${pathname}?${next.toString()}`;
};

export const useWorkspaceStage = (stages, fallbackId) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('stage');
  const requestedStage = stages.find((stage) => stage.id === requested);
  const prerequisite = requestedStage?.locked
    ? stages.find((stage) => stage.id === requestedStage.prerequisiteStage && !stage.locked)?.id
    : null;
  const firstUnlocked = stages.find((stage) => !stage.locked)?.id || fallbackId || stages[0]?.id;
  const activeStage = requestedStage && !requestedStage.locked ? requestedStage.id : prerequisite || firstUnlocked;

  useEffect(() => {
    if (!activeStage || requested === activeStage) return;
    const next = new URLSearchParams(searchParams);
    next.set('stage', activeStage);
    setSearchParams(next, { replace: true });
  }, [activeStage, requested, searchParams, setSearchParams]);

  const setStage = (stageId, options = {}) => {
    const next = new URLSearchParams(searchParams);
    next.set('stage', stageId);
    setSearchParams(next, options);
  };

  return [activeStage, setStage];
};

export const useUnsavedChangesWarning = (hasUnsavedChanges, message = 'Changes are still being saved. Leave this page?') => {
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const beforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', beforeUnload);
    const linkClick = (event) => {
      const link = event.target.closest?.('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin || destination.pathname === window.location.pathname) return;
      if (!window.confirm(message)) event.preventDefault();
    };
    document.addEventListener('click', linkClick, true);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', linkClick, true);
    };
  }, [hasUnsavedChanges, message]);
};

export const ForecastReadinessBadge = ({ status = WORKSPACE_READINESS.NOT_STARTED, issueCount = 0 }) => (
  <span className={`workspace-readiness workspace-readiness-${status}`}>
    {status === WORKSPACE_READINESS.READY ? <Check size={12} aria-hidden="true" /> : null}
    {status === WORKSPACE_READINESS.BLOCKED ? <Lock size={12} aria-hidden="true" /> : null}
    {status === WORKSPACE_READINESS.NEEDS_ATTENTION ? <AlertTriangle size={12} aria-hidden="true" /> : null}
    {READINESS_LABELS[status] || READINESS_LABELS[WORKSPACE_READINESS.NOT_STARTED]}
    {issueCount > 0 ? <span className="workspace-readiness-count">{issueCount}</span> : null}
  </span>
);

export const ForecastStageNav = ({ stages, activeStage, ariaLabel = 'Forecast workflow stages' }) => {
  const location = useLocation();
  return (
    <nav className="workspace-stage-nav" aria-label={ariaLabel}>
      <ol>
        {stages.map((stage, index) => {
          const destination = stage.locked && stage.prerequisiteStage ? stage.prerequisiteStage : stage.id;
          const label = stage.locked
            ? `${stage.label}. Locked: ${stage.lockedReason || 'Complete the prerequisite first.'}`
            : `${stage.label}. ${READINESS_LABELS[stage.status] || 'Not Started'}.`;
          return (
            <li key={stage.id}>
              <Link
                to={withStage(location.pathname, location.search, destination)}
                className={`workspace-stage-link ${activeStage === stage.id ? 'active' : ''} ${stage.locked ? 'locked' : ''}`}
                aria-current={activeStage === stage.id ? 'step' : undefined}
                aria-label={label}
                title={stage.locked
                  ? stage.lockedReason
                  : [READINESS_LABELS[stage.status] || 'Not Started',
                     stage.issueSummary ? `Waiting on: ${stage.issueSummary}` : (stage.exitCriteria ? `Done when: ${stage.exitCriteria}` : null)]
                      .filter(Boolean).join(' — ')}
              >
                <span className="workspace-stage-number">{index + 1}</span>
                <span className="workspace-stage-copy">
                  <strong>{stage.label}</strong>
                  <ForecastReadinessBadge status={stage.status} issueCount={stage.issueCount} />
                </span>
                {stage.locked ? <Lock size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export const ForecastImpactStrip = ({ items = [], ariaLabel = 'Live forecast impact' }) => {
  const hasGroupedItems = items.some((item) => item.metrics?.length || Number.isFinite(item.progress));

  return (
    <section className={`workspace-impact-strip ${hasGroupedItems ? 'grouped' : ''}`} aria-label={ariaLabel}>
      {items.map((item) => {
        const progress = Number.isFinite(item.progress)
          ? Math.max(0, Math.min(100, Math.round(item.progress)))
          : null;
        const progressTone = progress === null ? ''
          : progress >= 80 ? 'progress-green'
            : progress >= 50 ? 'progress-yellow' : 'progress-orange';
        const hasMetrics = Boolean(item.metrics?.length);
        const showStandaloneValue = !hasMetrics && progress === null;

        const classes = ['workspace-impact-item', item.tone || 'neutral'];
        if (hasMetrics) classes.push('metric-group');
        if (item.compact) classes.push('compact');
        if (progress !== null) classes.push('progress', progressTone);

        return (
          <div className={classes.join(' ')} key={item.id || item.label}>
            <span className="workspace-impact-label">{item.label}</span>
            {progress !== null ? (
              <>
                <strong>{progress}%</strong>
                <div
                  className="workspace-impact-progress"
                  role="progressbar"
                  aria-label={item.progressLabel || item.label}
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={progress}
                >
                  <span style={{ width: `${progress}%` }} />
                </div>
              </>
            ) : null}
            {hasMetrics ? (
              <div className="workspace-impact-metrics">
                {item.metrics.map((metric) => (
                  <div className={metric.tone || 'neutral'} key={metric.id || metric.label}>
                    <small className="workspace-impact-period">{metric.label}</small>
                    <strong>{metric.value}</strong>
                    {metric.comparison ? (
                      <small className="workspace-impact-comparison">
                        <b className={`workspace-impact-comparison-value ${metric.comparison.tone || 'neutral'}`}>
                          {metric.comparison.value}
                        </b>{' '}{metric.comparison.label}
                      </small>
                    ) : null}
                    {metric.detail ? <small>{metric.detail}</small> : null}
                  </div>
                ))}
              </div>
            ) : null}
            {showStandaloneValue ? <strong>{item.value}</strong> : null}
            {item.detail ? <small>{item.detail}</small> : null}
          </div>
        );
      })}
    </section>
  );
};

export const ForecastStageBrief = ({ stage }) => {
  if (!stage || (!stage.systemAction && !stage.userDecision && !stage.exitCriteria)) return null;
  const items = [
    { id: 'system', label: 'System prepared', value: stage.systemAction, Icon: Bot },
    { id: 'user', label: 'Your decision', value: stage.userDecision, Icon: UserCheck },
    { id: 'ready', label: 'Ready when', value: stage.exitCriteria, Icon: CheckCircle2 }
  ].filter((item) => item.value);

  return (
    <section className="workspace-stage-brief" aria-label={`${stage.label} responsibilities`}>
      {items.map(({ id, label, value, Icon }) => (
        <div key={id} className={`workspace-stage-brief-item ${id}`}>
          <Icon size={15} aria-hidden="true" />
          <div>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        </div>
      ))}
    </section>
  );
};

export const ForecastWorkspaceShell = ({
  eyebrow,
  title,
  description,
  backTo,
  backLabel,
  status,
  saveState,
  metadata = [],
  stages,
  activeStage,
  impactItems,
  planningTools,
  primaryAction,
  secondaryActions,
  children
}) => {
  const activeStageDefinition = stages.find((stage) => stage.id === activeStage);
  return (
    <div className="forecast-module forecast-workspace-shell">
    <a className="workspace-skip-link" href="#forecast-workspace-content">Skip to active stage</a>
    <header className="workspace-command-header">
      <div className="workspace-command-copy">
        <div className="forecast-kicker">
          {eyebrow}
          {backTo ? <Link to={backTo} className="forecast-kicker-back">← {backLabel || 'Back'}</Link> : null}
        </div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      <div className="workspace-command-actions">
        <div className="forecast-submit-chips">
          {status ? <span className="forecast-status-chip moss">{status}</span> : null}
          {saveState ? <span className={`forecast-health-chip ${/error|failed/i.test(saveState) ? 'red' : 'neutral'}`} aria-live="polite">{saveState}</span> : null}
        </div>
        <div className="workspace-action-row">
          {secondaryActions}
          {primaryAction}
        </div>
      </div>
      {metadata.length > 0 ? (
        <dl className="workspace-command-meta">
          {metadata.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value || '—'}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </header>
    <div className="workspace-sticky-stack">
      <ForecastStageNav stages={stages} activeStage={activeStage} />
      {impactItems?.length ? <ForecastImpactStrip items={impactItems} /> : null}
    </div>
    {planningTools ? <div className="workspace-planning-tools-slot">{planningTools}</div> : null}
    <ForecastStageBrief stage={activeStageDefinition} />
    <section className="workspace-stage-content" id="forecast-workspace-content" aria-label="Active forecast stage" tabIndex="-1">
      {children}
    </section>
    </div>
  );
};

export const ForecastAiProposalCard = ({ proposal, onAccept, onEdit, onReject, children }) => {
  const rawConfidence = Number(proposal.confidence);
  const confidence = proposal.confidence === undefined || proposal.confidence === null || !Number.isFinite(rawConfidence)
    ? 'Not scored'
    : `${Math.round(rawConfidence * (rawConfidence <= 1 ? 100 : 1))}%`;
  const displayValue = (value) => value ?? '—';
  return (
    <article className={`workspace-ai-proposal status-${proposal.status || 'pending'}`}>
      <div className="workspace-ai-proposal-head">
        <span className="workspace-ai-icon"><Sparkles size={15} aria-hidden="true" /></span>
        <div>
          <span className="forecast-section-code">AI Proposal · {confidence} confidence</span>
          <h3>{proposal.title}</h3>
        </div>
        {proposal.status ? (
          <span className={`forecast-health-chip mini ${{ accepted: 'green', rejected: 'red', edited: 'gold', manual: 'neutral' }[proposal.status] || 'yellow'}`}>
            {proposal.status}
          </span>
        ) : null}
      </div>
      {proposal.evidence ? <p>{proposal.evidence}</p> : null}
      <div className="workspace-proposal-values">
        <span><small>Current</small><strong>{displayValue(proposal.currentValue)}</strong></span>
        <ChevronRight size={16} aria-hidden="true" />
        <span><small>Proposed</small><strong>{displayValue(proposal.proposedValue)}</strong></span>
        {proposal.impact ? <span><small>Estimated Impact</small><strong>{proposal.impact}</strong></span> : null}
      </div>
      {proposal.rationale ? <p className="forecast-submit-note">{proposal.rationale}</p> : null}
      {proposal.reviewedBy && proposal.status && proposal.status !== 'pending' ? (
        <p className="forecast-submit-note" style={{ margin: 0 }}>
          {proposal.status} by {proposal.reviewedBy}
          {proposal.reviewedAt ? ` · ${new Date(proposal.reviewedAt).toLocaleString()}` : ''}
        </p>
      ) : null}
      {children}
      <div className="workspace-action-row">
        {onAccept ? <button type="button" className="forecast-primary-button compact" onClick={() => onAccept(proposal)}>Accept</button> : null}
        {onEdit ? <button type="button" className="forecast-secondary-button compact" onClick={() => onEdit(proposal)}>Edit</button> : null}
        {onReject ? <button type="button" className="forecast-secondary-button compact" onClick={() => onReject(proposal)}>Reject</button> : null}
      </div>
    </article>
  );
};

export const ForecastValidationList = ({ issues = [], emptyLabel = 'No blocking issues.', onNavigate }) => (
  <div className="workspace-validation-list" aria-live="polite">
    {issues.length === 0 ? <p className="workspace-empty-state"><Check size={15} aria-hidden="true" /> {emptyLabel}</p> : (
      <ul>
        {issues.map((issue) => (
          <li className={`severity-${issue.severity || 'warning'}`} key={issue.id}>
            <AlertTriangle size={15} aria-hidden="true" />
            <div><strong>{issue.label}</strong>{issue.detail ? <small>{issue.detail}</small> : null}</div>
            {onNavigate ? <button type="button" className="forecast-secondary-button compact" onClick={() => onNavigate(issue)}>Resolve</button> : null}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const ForecastChangeDrawer = ({ title = 'Change History', entries = [], children }) => {
  const content = useMemo(() => entries.slice(0, 30), [entries]);
  return (
    <details className="workspace-change-drawer">
      <summary><History size={15} aria-hidden="true" /> {title} <span>{entries.length}</span></summary>
      <div className="workspace-change-content">
        {children}
        {content.length === 0 ? <p className="forecast-submit-note">No recorded changes yet.</p> : (
          <ol>
            {content.map((entry, index) => (
              <li key={entry.id || `${entry.event || 'change'}-${index}`}>
                <strong>{entry.event || entry.label || 'Change'}</strong>
                <span>{entry.actor || entry.by || 'System'}</span>
                {entry.comment ? <p>{entry.comment}</p> : null}
                {entry.created_at || entry.at ? <time>{new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(entry.created_at || entry.at))}</time> : null}
              </li>
            ))}
          </ol>
        )}
      </div>
    </details>
  );
};
